import { AuditorPolicyReviewHistory } from '@/components/auditor-center/AuditorPolicyReviewHistory'
import { invalidatePolicyQueries } from '@/utils/policy-cache'
import { PolicyReviewAction } from '@/components/screenata/policy-detail/PolicyReviewAction'
import type { AuditorPolicyDetailDTO } from '@deepguide-ai/types'
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import PolicyDetailPanel from '@/pages/screenata/policies/components/PolicyDetailPanel'
import PolicySubmissionMetaBar from '@/pages/screenata/policies/components/PolicySubmissionMetaBar'
import { getAuditorSubmissionStatusPresentation } from '@/pages/screenata/policies/get-auditor-submission-status-presentation'
import type { PolicySubmissionMeta } from '@/pages/screenata/policies/types'
import { MainToolbar } from '@/tiptap/editor/components/main-toolbar'
import { renderToMarkdown } from '@/tiptap/markdown/renderToMarkdown'
import { canEditVisiblePolicyVersion } from '@deepguide-ai/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { PluginKey } from '@tiptap/pm/state'
import {
  EditorContext,
  type Editor,
  type EditorContextValue,
} from '@tiptap/react'
import { TRPCClientError } from '@trpc/client'
import {
  ArrowLeft,
  AlertTriangle,
  Compass,
  FileText,
  Loader2,
  Save,
  Shield,
  Zap,
  Check,
  X,
  Copy,
  History,
  RefreshCw,
  Sparkles,
  MessageSquare,
} from 'lucide-react'
import { Eye } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import {
  usePolicyVersionUpload,
  POLICY_UPLOAD_ACCEPT,
} from '@/hooks/usePolicyVersionUpload'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { usePublishViewModeBanner } from '@/components/ViewModeBanner'
import { AdminAuditorViewExitLink } from '@/components/auditor-center/AdminAuditorViewBanner'
import { useAuditorCenterAdminPreview } from '@/components/auditor-center/AuditorCenterCycleShell'
import { AuditorCycleBreadcrumb } from '@/components/auditor-center/AuditorCycleBreadcrumb'
import { PolicyGalaxyLineageDialog } from '@/components/compliance-lineage/PolicyGalaxyLineageDialog'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import {
  PolicyContentViewer,
  hasRenderablePolicyContent,
} from '@/components/screenata/PolicyContentViewer'
import { PolicyDownloadActions } from '@/components/screenata/PolicyDownloadActions'
import { SystemDescriptionTemplateBanner } from '@/components/screenata/SystemDescriptionTemplateBanner'
import {
  buildPolicyClaimBlockMaps,
  registerPolicyClaimBadgeDecorations,
  scrollToPolicyClaimBlock,
  syncPolicyClaimBlockDecorations,
} from '@/components/screenata/policy-claim-blocks'
import {
  type GenerationItem,
  type PolicyItem,
  type TriageClaim,
  type PolicyClaimHighlight,
  PolicyDetailHeader,
  PolicyDetailDocumentSkeleton,
  PolicyDetailLoadingShell,
  POLICY_DETAIL_PAGE_BODY_CLASS,
  usePolicyDetailQueries,
  useApprovePolicy,
  usePolicyEditorEvents,
  usePolicyGeneratedEvent,
} from '@/components/screenata/policy-detail'
import {
  ApprovePolicyDialog,
  POLICY_APPROVAL_GATE_FLAG,
} from '@/components/screenata/policy-detail/ApprovePolicyDialog'
import {
  CoachTour,
  domAnchorPresent,
  useCoachTour,
  type CoachStep,
} from '@/components/tour/CoachTour'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/policies_/$policySlug'
)({
  staticData: {
    pageHeader: { title: 'Policy', customHeader: true, pad: false },
  },
  beforeLoad: ({ params, search }) => {
    if (params.policySlug === 'system-description') {
      throw redirect({
        to: '/workspaces/$workspaceId/system-description',
        params: { workspaceId: params.workspaceId },
        search: search.testId ? { testId: search.testId } : undefined,
        replace: true,
      })
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    const parsePositiveVersion = (value: unknown) => {
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.floor(value)
      }
      if (typeof value === 'string' && /^\d+$/.test(value)) {
        const parsed = parseInt(value, 10)
        if (parsed > 0) return parsed
      }
      return undefined
    }
    const parseNonEmptyString = (value: unknown) =>
      typeof value === 'string' && value.trim().length > 0 ? value : undefined
    return {
      regenerate:
        search.regenerate === true || search.regenerate === 'true' || undefined,
      version: parsePositiveVersion(search.version),
      claimId: parseNonEmptyString(search.claimId),
      blockId: parseNonEmptyString(search.blockId),
      testId: parseNonEmptyString(search.testId),
    }
  },
  component: PolicyDetailRoutePage,
})

// ── Page Component ──

const POLICY_EDIT_LOCK_RENEW_INTERVAL_MS = 60_000

type ActivePolicyEditLock = {
  policyDocumentId: string
  lockId: string
}

function PolicyDetailRoutePage() {
  const { workspaceId, policySlug } = useParams({
    from: '/(protected)/workspaces/$workspaceId/policies_/$policySlug',
  })
  const {
    regenerate: regenerateParam,
    version: versionParam,
    claimId: focusedClaimId,
    blockId: focusedClaimBlockId,
    testId,
  } = useSearch({
    from: '/(protected)/workspaces/$workspaceId/policies_/$policySlug',
  })
  const navigate = useNavigate()

  const viewingVersion = versionParam ?? null
  const handleViewingVersionChange = useCallback(
    (next: number | null) => {
      navigate({
        to: '/workspaces/$workspaceId/policies/$policySlug',
        params: { workspaceId, policySlug },
        search: (prev: Record<string, unknown> | undefined) => {
          const search = { ...(prev ?? {}) } as Record<string, unknown>
          search.version = next ?? undefined
          return search
        },
        replace: true,
      })
    },
    [navigate, workspaceId, policySlug]
  )
  return (
    <SharedPolicyDetailPage
      workspaceId={workspaceId}
      policySlug={policySlug}
      testId={testId}
      backHref={`/workspaces/${workspaceId}/policies`}
      autoRegenerate={!!regenerateParam}
      onAutoRegenerateTriggered={() => {
        // Clear the ?regenerate param from URL after triggering, but
        // preserve any other search params (e.g. ?version=N).
        navigate({
          to: '/workspaces/$workspaceId/policies/$policySlug',
          params: { workspaceId, policySlug },
          search: (prev: Record<string, unknown> | undefined) => {
            const next = { ...(prev ?? {}) } as Record<string, unknown>
            delete next.regenerate
            return next
          },
          replace: true,
        })
      }}
      viewingVersion={viewingVersion}
      onViewingVersionChange={handleViewingVersionChange}
      focusedClaimId={focusedClaimId}
      focusedClaimBlockId={focusedClaimBlockId}
    />
  )
}

export function SharedPolicyDetailPage({
  workspaceId,
  policySlug,
  mode = 'manage',
  panelMode = false,
  testId,
  backHref,
  auditCycleId,
  submissionMeta,
  auditorPolicy,
  submissionMetaLoading = false,
  onReviewSubmission,
  reviewSubmissionPending = false,
  enableComments = true,
  hideCommentComposer = false,
  autoRegenerate = false,
  onAutoRegenerateTriggered,
  viewingVersion: viewingVersionProp,
  onViewingVersionChange: onViewingVersionChangeProp,
  focusedClaimId,
  focusedClaimBlockId,
}: {
  workspaceId: string
  policySlug: string
  mode?: 'manage' | 'readonly'
  /** Main-panel overlay: fill parent height instead of viewport calc. */
  panelMode?: boolean
  testId?: string
  backHref: string
  /** When set, header breadcrumb matches auditor-center shell (Audits > Policies > title). */
  auditCycleId?: string
  submissionMeta?: PolicySubmissionMeta
  auditorPolicy?: AuditorPolicyDetailDTO
  /** Auditor `getPolicyDetails` — merged into auditor detail loading shell. */
  submissionMetaLoading?: boolean
  onReviewSubmission?: (
    decision: 'APPROVED' | 'NEEDS_REVISION',
    comments?: string
  ) => void
  reviewSubmissionPending?: boolean
  enableComments?: boolean
  hideCommentComposer?: boolean
  autoRegenerate?: boolean
  onAutoRegenerateTriggered?: () => void
  /** When supplied, version selection is controlled by the caller (e.g.,
   * synced with a `?version=N` URL query param). Otherwise an internal
   * useState backs the value. */
  viewingVersion?: number | null
  onViewingVersionChange?: (version: number | null) => void
  focusedClaimId?: string
  focusedClaimBlockId?: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Policy edits are for OWNER/ADMIN/CONTRIBUTOR — mirror the server
  // policyWriteProcedure gate (`canWorkspaceWrite`, i.e. CONTRIBUTOR+admin+owner).
  // VIEWER/AUDITOR/EMPLOYEE get the read-only view: no editing, metadata changes,
  // claim edits, regenerate, or upload. Flip to read-only only after permissions
  // resolve so writers don't see a layout flash. (Previously aliased `canReview`,
  // which is admin-only, so contributors were wrongly locked out of editing.)
  const {
    isAdmin,
    isOwner,
    canWorkspaceWrite: canWritePolicy,
    isLoading: policyPermsLoading,
  } = useWorkspacePermissions(workspaceId)
  const isReadonlyMode =
    mode === 'readonly' || (!policyPermsLoading && !canWritePolicy)
  const [internalViewingVersion, setInternalViewingVersion] = useState<
    number | null
  >(null)
  const [internalCompareVersion, setInternalCompareVersion] = useState<
    number | null
  >(null)
  const isVersionControlled = viewingVersionProp !== undefined
  const viewingVersion = isVersionControlled
    ? (viewingVersionProp ?? null)
    : internalViewingVersion
  const compareVersion = internalCompareVersion
  const setViewingVersion = useCallback(
    (next: number | null) => {
      if (isVersionControlled) {
        onViewingVersionChangeProp?.(next)
      } else {
        setInternalViewingVersion(next)
      }
      setInternalCompareVersion(null)
    },
    [isVersionControlled, onViewingVersionChangeProp]
  )
  const setCompareVersion = useCallback((next: number | null) => {
    setInternalCompareVersion(next)
  }, [])
  const [showSubmissionReviewDialog, setShowSubmissionReviewDialog] =
    useState(false)
  const [submissionReviewComment, setSubmissionReviewComment] = useState('')
  const [openCommentThreadCount, setOpenCommentThreadCount] = useState(0)
  const [preferredSidebarTab, setPreferredSidebarTab] = useState<
    'properties' | 'claims' | 'comments' | 'assumptions'
  >('properties')
  const [showClaims, setShowClaims] = useState(false)
  const [showHighlights, setShowHighlights] = useState(true)
  const [showPolicyLineage, setShowPolicyLineage] = useState(false)
  const [compareDialogOpenRequest, setCompareDialogOpenRequest] = useState(0)
  const editorRef = useRef<any>(null)
  const [policyEditorToolbar, setPolicyEditorToolbar] = useState<{
    editor: Editor
    onOpenComments?: () => void
    onAskVera: (payload: { quoteText?: string; docBlockId?: string }) => void
    onAddClaimFromSelection?: (payload: {
      docBlockId: string
      quoteText: string
      offset: number
    }) => void
  } | null>(null)
  const [activePolicyEditLock, setActivePolicyEditLock] =
    useState<ActivePolicyEditLock | null>(null)
  const activePolicyEditLockRef = useRef<ActivePolicyEditLock | null>(null)
  const activatePolicyEditLock = useCallback((lock: ActivePolicyEditLock) => {
    activePolicyEditLockRef.current = lock
    setActivePolicyEditLock(lock)
  }, [])
  const clearActivePolicyEditLock = useCallback((expectedLockId?: string) => {
    if (
      expectedLockId &&
      activePolicyEditLockRef.current?.lockId !== expectedLockId
    ) {
      return false
    }
    activePolicyEditLockRef.current = null
    setActivePolicyEditLock(null)
    return true
  }, [])
  const isDocumentUnloadingRef = useRef(false)
  const adminAuditorPreview = useAuditorCenterAdminPreview()

  // Reached in auditor-preview mode but rendered outside the cycle shell, so it
  // publishes the banner itself rather than tinting its own header.
  usePublishViewModeBanner(
    'preview',
    adminAuditorPreview
      ? {
          icon: Eye,
          label: 'Auditor preview',
          description: 'This is what your auditor sees on your submission',
          action: (
            <AdminAuditorViewExitLink
              workspaceId={adminAuditorPreview.workspaceId}
              auditCycleId={adminAuditorPreview.auditCycleId}
            />
          ),
        }
      : null,
    [adminAuditorPreview]
  )
  const isAuditorCenterPolicy = !!auditCycleId
  const auditsHomeRoute =
    isAdmin || isOwner
      ? '/workspaces/$workspaceId/audits'
      : '/workspaces/$workspaceId/auditor-center'

  const { programId, items, policies, isLoading } = usePolicyDetailQueries()
  // Upload your own document as a new version (or first version) of this policy.
  const { upload: uploadPolicyVersion, uploadingSlug } =
    usePolicyVersionUpload(programId)
  const versionUploadInputRef = useRef<HTMLInputElement>(null)

  const generationItem =
    items.find((i) => i.slug === policySlug) ??
    items.find((i) => i.policyDocumentId === policySlug) ??
    null
  // The editor keeps its customer-side model; auditor-visible metadata and
  // content come only from the approved, cycle-scoped read projection.
  const item = generationItem && auditorPolicy
    ? {
        ...generationItem,
        title: auditorPolicy.title,
        slug: auditorPolicy.slug,
        policyDocumentId: auditorPolicy.policyDocumentId,
        controlRefs: auditorPolicy.linkedControls,
        frameworkRefs: auditorPolicy.frameworkRefs,
        mappedControls: auditorPolicy.frameworkRefs.flatMap(({ framework, refs }) =>
          refs.map((ref) => ({ framework, ref, title: null }))),
        policyStatus: 'approved',
        generationState: 'approved' as const,
        currentVersion: auditorPolicy.submittedVersion,
        approvedVersion: auditorPolicy.submittedVersion,
      }
    : generationItem
  const workingPolicy = policies.find((p) => p.slug === item?.slug)
  const policyData = auditorPolicy
    ? {
        ...workingPolicy,
        generated: true,
        status: 'approved' as const,
        approvedAt: auditorPolicy.internalApprovedAt?.toISOString() ?? null,
        approvedByName: auditorPolicy.internalApprovedBy,
        ownerRoleId: workingPolicy?.ownerRoleId ?? null,
        reviewFrequency: workingPolicy?.reviewFrequency ?? null,
        nextReviewDate: workingPolicy?.nextReviewDate ?? null,
      }
    : workingPolicy
  const ownerReviewMeta: PolicySubmissionMeta | undefined =
    !auditCycleId && item?.approvedVersion != null
      ? {
          submissionId: item.submissionId ?? null,
          submittedVersion: item.submittedVersion ?? item.approvedVersion,
          submissionStatus: item.submissionStatus ?? null,
          submittedAt: item.submittedAt ?? item.lastApprovedAt ?? null,
          submissionReviewedAt: item.submissionReviewedAt ?? null,
          submissionReviewedBy: item.submissionReviewedBy ?? null,
          submissionReviewComments: item.submissionReviewComments ?? null,
          auditCycleId: item.auditCycleId ?? null,
          auditCycleLabel: item.auditCycleLabel ?? null,
        }
      : undefined
  const visibleSubmissionMeta = submissionMeta ?? ownerReviewMeta

  // Derive editing state: drafts are always editable, approved versions are read-only.
  const versionsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyVersions.queryOptions({
      policyDocumentId: item?.policyDocumentId ?? '',
    }),
    enabled: !!item?.policyDocumentId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const versionsData = (versionsQuery.data as any)?.versions as
    | { version: number; approvalSignature: unknown }[]
    | undefined
  const displayVersionsData =
    isReadonlyMode && submissionMeta?.submittedVersion != null
      ? (versionsData?.filter(
          (version) => version.version === submissionMeta.submittedVersion
        ) ?? [])
      : (versionsData ?? [])
  const currentVersionFromQuery = auditorPolicy?.submittedVersion ??
    ((versionsQuery.data as any)?.currentVersion as number | undefined)
  const visibleVersionNumber = viewingVersion ?? currentVersionFromQuery ?? null
  const isViewingApprovedVersion =
    viewingVersion !== null &&
    !!versionsData?.find((v) => v.version === viewingVersion)?.approvalSignature
  const isCurrentVersionDraft =
    currentVersionFromQuery != null &&
    currentVersionFromQuery > 0 &&
    !versionsData?.find((v) => v.version === currentVersionFromQuery)
      ?.approvalSignature
  const versionsReady = !item?.policyDocumentId || versionsQuery.isFetched
  const isBlankPolicy =
    versionsReady &&
    !!item?.policyDocumentId &&
    (currentVersionFromQuery == null || currentVersionFromQuery === 0)
  const isPendingApproval =
    (policyData?.status ?? item?.policyStatus) === 'pending_approval'
  // Composed from the shared rule (@deepguide-ai/types) so the client can no
  // longer enforce a different editability policy than the server — the
  // drift that made Apply acquire a lock and then refuse to enter edit mode.
  const canEditVisiblePolicy = canEditVisiblePolicyVersion({
    status: policyData?.status ?? item?.policyStatus,
    viewingApproved: viewingVersion !== null && !!isViewingApprovedVersion,
    currentIsDraft: viewingVersion !== null || !!isCurrentVersionDraft,
    isBlank: isBlankPolicy,
    readOnly: isReadonlyMode,
  })
  const currentActivePolicyEditLock = activePolicyEditLock
  const activePolicyEditLockId =
    currentActivePolicyEditLock &&
    item?.policyDocumentId &&
    currentActivePolicyEditLock.policyDocumentId === item.policyDocumentId
      ? currentActivePolicyEditLock.lockId
      : null
  const isEditing = canEditVisiblePolicy && !!activePolicyEditLockId
  const policyEditLockStatusQuery = useQuery({
    ...trpc.complianceProgram.getPolicyEditLockStatus.queryOptions({
      policyDocumentId: item?.policyDocumentId ?? '',
    }),
    enabled: !!item?.policyDocumentId && !activePolicyEditLockId,
    refetchInterval: 3_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const isLockedByAnotherUser =
    !!policyEditLockStatusQuery.data?.locked && !activePolicyEditLockId
  const policyEditLockBannerText = isLockedByAnotherUser
    ? `${policyEditLockStatusQuery.data?.lockedByName ?? 'Another user'} is editing this policy. This document is read-only until they finish.`
    : null

  useEffect(() => {
    const status = policyEditLockStatusQuery.data
    if (
      !item?.policyDocumentId ||
      !status?.ownedByCurrentUser ||
      !status.lockId ||
      activePolicyEditLockRef.current
    ) {
      return
    }
    const lock = {
      policyDocumentId: item.policyDocumentId,
      lockId: status.lockId,
    }
    activatePolicyEditLock(lock)
  }, [
    activatePolicyEditLock,
    item?.policyDocumentId,
    policyEditLockStatusQuery.data,
  ])

  // Triage claims — used by sidebar panel. When viewing a historical
  // version we filter to that version's stamped claims (typically marked
  // stale after rollover, which the server explicitly allows for
  // version-scoped queries).
  const claimsQuery = useQuery({
    ...trpc.claimTriage.list.queryOptions({
      programId: programId ?? '',
      policyName: policySlug,
      ...(viewingVersion !== null && item?.policyDocumentId
        ? {
            policyDocumentId: item.policyDocumentId,
            version: viewingVersion,
          }
        : {}),
    }),
    // Wait for policyDocumentId before issuing the request when a
    // version is selected, so we don't briefly fetch the unscoped
    // (latest) result and then refetch.
    enabled:
      !!programId && (viewingVersion === null || !!item?.policyDocumentId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  const claims = useMemo(
    () => (claimsQuery.data ?? []) as TriageClaim[],
    [claimsQuery.data]
  )

  // Draft claims — same source as generate-policies page, used for header
  // badge count. Only relevant for the live current draft, never for a
  // historical version snapshot.
  const draftClaimsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDraftClaims.queryOptions({
      programId: programId ?? '',
      policySlug,
    }),
    enabled: !!programId && !!policySlug && viewingVersion === null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  const draftClaims = useMemo(
    () => (draftClaimsQuery.data?.claims ?? []) as any[],
    [draftClaimsQuery.data]
  )
  const submittedSnapshotClaims = useMemo(
    () =>
      isReadonlyMode
        ? (((submissionMeta?.claimList ?? []) as any[]) ?? [])
        : [],
    [isReadonlyMode, submissionMeta?.claimList]
  )
  // Use whichever source has data for the count
  const headerClaims = isReadonlyMode
    ? submittedSnapshotClaims
    : draftClaims.length > 0
      ? draftClaims
      : claims

  const policyLineageQuery = useQuery({
    ...trpc.lineage.getPolicyChain.queryOptions({
      programId: programId ?? '',
      policySlug,
    }),
    enabled: showPolicyLineage && !!programId && !!policySlug,
  })

  const versionContentQuery = useQuery({
    ...trpc.complianceProgram.getPolicyVersionContent.queryOptions({
      policyDocumentId: item?.policyDocumentId ?? '',
      version: viewingVersion ?? 0,
    }),
    enabled: !!item?.policyDocumentId && viewingVersion !== null,
  })

  const compareBaselineVersion =
    isReadonlyMode && viewingVersion === null
      ? (submissionMeta?.submittedVersion ?? null)
      : viewingVersion
  const isComparingVersions =
    compareVersion !== null && compareVersion !== compareBaselineVersion
  const compareVersionContentQuery = useQuery({
    ...trpc.complianceProgram.getPolicyVersionContent.queryOptions({
      policyDocumentId: item?.policyDocumentId ?? '',
      version: compareVersion ?? 0,
    }),
    enabled: !!item?.policyDocumentId && isComparingVersions,
  })

  const submittedVersionContentQuery = useQuery({
    ...trpc.complianceProgram.getPolicyVersionContent.queryOptions({
      policyDocumentId: item?.policyDocumentId ?? '',
      version: submissionMeta?.submittedVersion ?? 0,
    }),
    enabled:
      !isAuditorCenterPolicy &&
      isReadonlyMode &&
      !!item?.policyDocumentId &&
      viewingVersion === null &&
      submissionMeta?.submittedVersion != null,
  })

  const saveMutation = useMutation(
    trpc.complianceProgram.updatePolicyContent.mutationOptions()
  )
  const beginPolicyEditMutation = useMutation(
    trpc.complianceProgram.beginPolicyEdit.mutationOptions()
  )
  const { mutateAsync: savePolicyContentMutation } = saveMutation
  const { mutateAsync: beginPolicyEditRequest } = beginPolicyEditMutation
  const releasePolicyEditLockMutation = useMutation(
    trpc.complianceProgram.releasePolicyEditLock.mutationOptions()
  )
  const renewPolicyEditLockMutation = useMutation(
    trpc.complianceProgram.renewPolicyEditLock.mutationOptions()
  )
  const releasePolicyEditLockMutateRef = useRef(
    releasePolicyEditLockMutation.mutateAsync
  )
  const renewPolicyEditLockMutateRef = useRef(
    renewPolicyEditLockMutation.mutateAsync
  )
  const revertMutation = useMutation(
    trpc.complianceProgram.revertToVersion.mutationOptions()
  )

  const { approveMutation, handleApprove } = useApprovePolicy({
    programId,
    policySlug,
    policyDocumentId: item?.policyDocumentId,
  })
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  // #850/#1002 approval workflow is dark-launched behind this flag.
  const { enabled: approvalGateEnabled } = useFeatureFlag(
    POLICY_APPROVAL_GATE_FLAG
  )

  const releaseActivePolicyEditLock = useCallback(async () => {
    const lock = activePolicyEditLockRef.current
    if (!lock) return true
    try {
      await releasePolicyEditLockMutateRef.current(lock)
      clearActivePolicyEditLock(lock.lockId)
      return true
    } catch {
      return false
    }
  }, [clearActivePolicyEditLock])

  const beginPolicyEdit = useCallback(async () => {
    if (!item?.policyDocumentId) return false
    try {
      const result = await beginPolicyEditRequest({
        policyDocumentId: item.policyDocumentId,
      })
      if (!result.acquired || !result.lockId) {
        if (result.lockedByName) {
          toast.error(
            `${result.lockedByName} is editing this policy. You can view it, but editing is locked.`
          )
        } else {
          toast.error('Could not start editing. Please try again.')
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyEditLockStatus.queryKey({
            policyDocumentId: item.policyDocumentId,
          }),
        })
        return false
      }

      const lock = {
        policyDocumentId: item.policyDocumentId,
        lockId: result.lockId,
      }
      activatePolicyEditLock(lock)
      if (result.created) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
              policyDocumentId: item.policyDocumentId,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
              policyDocumentId: item.policyDocumentId,
            }),
          }),
          invalidatePolicyQueries(queryClient),
          queryClient.invalidateQueries({
            queryKey: trpc.compliance.getRecommendedEvidence.queryKey(),
          }),
        ])
      }
      return true
    } catch (error) {
      // The global tRPC mutation handler (lib/trpc.ts) already surfaces the
      // server's own message — e.g. "Policy is pending approval. Return it
      // for changes before editing." Adding a generic toast on top of it is
      // what produced a stack of three errors for one cause when Vera's
      // "Apply" hit a pending-approval policy. Only speak when nothing else
      // did (e.g. a non-tRPC failure in the invalidation pass).
      if (!(error instanceof TRPCClientError)) {
        toast.error('Failed to start editing policy')
      }
      return false
    }
  }, [
    activatePolicyEditLock,
    beginPolicyEditRequest,
    item?.policyDocumentId,
    queryClient,
  ])

  useEffect(() => {
    releasePolicyEditLockMutateRef.current =
      releasePolicyEditLockMutation.mutateAsync
  }, [releasePolicyEditLockMutation.mutateAsync])

  useEffect(() => {
    renewPolicyEditLockMutateRef.current =
      renewPolicyEditLockMutation.mutateAsync
  }, [renewPolicyEditLockMutation.mutateAsync])

  useEffect(() => {
    const markDocumentUnloading = () => {
      isDocumentUnloadingRef.current = true
    }
    const markDocumentActive = () => {
      isDocumentUnloadingRef.current = false
    }
    window.addEventListener('beforeunload', markDocumentUnloading)
    window.addEventListener('pagehide', markDocumentUnloading)
    window.addEventListener('pageshow', markDocumentActive)
    return () => {
      window.removeEventListener('beforeunload', markDocumentUnloading)
      window.removeEventListener('pagehide', markDocumentUnloading)
      window.removeEventListener('pageshow', markDocumentActive)
    }
  }, [])

  useEffect(() => {
    const lock = activePolicyEditLock
    if (!lock) return

    const renewLock = async () => {
      try {
        const result = await renewPolicyEditLockMutateRef.current(lock)
        if (
          !result.renewed &&
          activePolicyEditLockRef.current?.lockId === lock.lockId
        ) {
          clearActivePolicyEditLock(lock.lockId)
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyEditLockStatus.queryKey({
              policyDocumentId: lock.policyDocumentId,
            }),
          })
          toast.error('Your editing session ended. Reopen Edit to continue.')
        }
      } catch {
        // A transient renewal failure is retried; the server TTL remains the
        // final authority for whether this session still owns the lock.
      }
    }

    void renewLock()
    const interval = window.setInterval(
      renewLock,
      POLICY_EDIT_LOCK_RENEW_INTERVAL_MS
    )
    return () => window.clearInterval(interval)
  }, [activePolicyEditLock, clearActivePolicyEditLock, queryClient])

  useEffect(() => {
    const lock = activePolicyEditLockRef.current
    if (lock && lock.policyDocumentId !== item?.policyDocumentId) {
      void releaseActivePolicyEditLock()
    }
  }, [item?.policyDocumentId, releaseActivePolicyEditLock])

  useEffect(() => {
    return () => {
      const lock = activePolicyEditLockRef.current
      if (!lock || isDocumentUnloadingRef.current) return
      void releasePolicyEditLockMutateRef.current(lock)
    }
  }, [])

  // Apply-block edits from chat. When the policy is on an approved (read-only)
  // version, branch an editable draft first so the edit lands on a new draft
  // instead of mutating the approved version. Mirrors the header Edit toggle.
  usePolicyEditorEvents(editorRef, {
    policySlug,
    isEditing,
    ensureEditable: async () => {
      const ok = activePolicyEditLockId ? true : await beginPolicyEdit()
      if (ok) setViewingVersion(null)
      return ok
    },
  })

  const handleRevert = () => {
    if (!item?.policyDocumentId || viewingVersion === null)
      return // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(revertMutation.mutate as any)(
      {
        policyDocumentId: item.policyDocumentId,
        version: viewingVersion,
      },
      {
        onSuccess: () => {
          toast.success(`Created new draft from v${viewingVersion}`)
          setViewingVersion(null)
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
              policyDocumentId: item.policyDocumentId!,
            }),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
              policyDocumentId: item.policyDocumentId!,
            }),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyCommitments.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyClaims.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.claimTriage.list.queryKey(),
          })
          invalidatePolicyQueries(queryClient)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to revert to version')
        },
      }
    )
  }

  // Auto-save: debounced save on editor changes when in draft editing mode
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The tail of the save queue. Each save installs its queued promise before
  // awaiting the previous tail, so autosave and final flush writes cannot fan
  // out concurrently after the same request finishes.
  const pendingSaveRef = useRef<Promise<unknown> | null>(null)
  const loadedPolicyDocumentIdRef = useRef<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle'
  )
  // True while flushing + approving, so the Approve button stays disabled
  // across the flush round-trip (before `approveMutation.isPending` flips).
  const [isFinalizingApproval, setIsFinalizingApproval] = useState(false)
  const [isEndingPolicyEdit, setIsEndingPolicyEdit] = useState(false)
  const isEndingPolicyEditRef = useRef(false)
  // Persist the editor's current content now. Shared by the debounced
  // autosave and the pre-approval flush. Waits for any in-flight save first
  // so this write lands last.
  const savePolicyContent = useCallback(
    async (tiptapJson: unknown) => {
      const policyDocumentId = item?.policyDocumentId
      if (
        !policyDocumentId ||
        loadedPolicyDocumentIdRef.current !== policyDocumentId
      )
        return
      const previousSave = pendingSaveRef.current
      const queuedSave = (async () => {
        if (previousSave) {
          try {
            await previousSave
          } catch {
            /* prior save failure handled by its own catch */
          }
        }
        setSaveStatus('saving')
        return (savePolicyContentMutation as any)({
          policyDocumentId,
          tiptapJson,
        })
      })()
      // Install this save as the queue tail synchronously. A later autosave or
      // final flush will wait for this promise, even while it is waiting for
      // `previousSave` itself.
      pendingSaveRef.current = queuedSave
      try {
        await queuedSave
        // Keep the query cache aligned with the successful server write so a
        // direct navigation back to this policy cannot render stale content.
        // The live editor uses `initial-only` synchronization while editing,
        // so this cache update cannot replace its document or move the cursor.
        queryClient.setQueryData(
          trpc.complianceProgram.getPolicyDocContent.queryKey({
            policyDocumentId,
          }),
          (old: unknown) =>
            old && typeof old === 'object'
              ? { ...(old as Record<string, unknown>), tiptapJson }
              : old
        )
        setSaveStatus('saved')
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
            policyDocumentId,
          }),
        })
        queryClient.invalidateQueries({
          queryKey: trpc.claimTriage.list.queryKey(),
        })
        return true
      } catch {
        setSaveStatus('idle')
        return false
      } finally {
        if (pendingSaveRef.current === queuedSave) pendingSaveRef.current = null
      }
    },
    [item?.policyDocumentId, savePolicyContentMutation, queryClient]
  )
  const handleEditorUpdate = useCallback(
    ({ editor }: { editor: any }) => {
      if (
        !isEditing ||
        !item?.policyDocumentId ||
        loadedPolicyDocumentIdRef.current !== item.policyDocumentId
      )
        return
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      const saveWhenCommentDraftCloses = () => {
        if (editor.storage.comment?.pendingNewComment) {
          autoSaveTimerRef.current = setTimeout(
            saveWhenCommentDraftCloses,
            2000
          )
          return
        }
        void savePolicyContent(editor.getJSON())
      }
      autoSaveTimerRef.current = setTimeout(saveWhenCommentDraftCloses, 2000)
    },
    [isEditing, item?.policyDocumentId, savePolicyContent]
  )
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  // Persist any pending editor changes immediately, bypassing the autosave
  // debounce. Approval snapshots the server-saved draft content, so a quick
  // Apply→Approve (within the 2s debounce) would otherwise drop the just-
  // applied edits. Flushing first guarantees the approved version includes them.
  // Gated on `editor.isEditable` (the real editor state) rather than the
  // `isEditing` flag, which can lag a query refetch on the apply-then-branch
  // path and otherwise skip the flush.
  const flushPendingPolicySave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
      autoSaveTimerRef.current = null
    }
    const editor = editorRef.current
    if (
      !item?.policyDocumentId ||
      !editor?.isEditable ||
      loadedPolicyDocumentIdRef.current !== item.policyDocumentId
    )
      return
    // savePolicyContent syncs the content cache, so the post-approval editor
    // re-init reads the just-applied edits rather than stale pre-edit content.
    const saved = await savePolicyContent(editor.getJSON())
    if (!saved) {
      throw new Error(
        'Could not save your latest changes. Approval was not requested.'
      )
    }
  }, [item?.policyDocumentId, savePolicyContent])

  const endPolicyEdit = useCallback(async () => {
    if (!activePolicyEditLockRef.current) return true
    if (isEndingPolicyEditRef.current) return false
    isEndingPolicyEditRef.current = true
    setIsEndingPolicyEdit(true)
    try {
      await flushPendingPolicySave()
      const released = await releaseActivePolicyEditLock()
      if (!released) {
        throw new Error('Could not release the editing session')
      }
      return true
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not end the editing session'
      )
      return false
    } finally {
      isEndingPolicyEditRef.current = false
      setIsEndingPolicyEdit(false)
    }
  }, [flushPendingPolicySave, releaseActivePolicyEditLock])

  const handleCopyMarkdown = () => {
    const editor = editorRef.current
    if (!editor) return
    try {
      const raw = renderToMarkdown(editor.getJSON())
      // Strip internal block-id directives (:dg{id="..."})
      const markdown = raw.replace(/ ?:dg\{id="[^"]*"\}/g, '')
      navigator.clipboard.writeText(markdown)
      toast.success('Copied as Markdown')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const handleApproveSubmission = () => {
    onReviewSubmission?.(
      'APPROVED',
      submissionReviewComment.trim() || undefined
    )
    setShowSubmissionReviewDialog(false)
    setSubmissionReviewComment('')
  }

  const handleRequestSubmissionChanges = () => {
    const comment = submissionReviewComment.trim()
    if (!comment) {
      toast.error('Review comments are required when requesting changes')
      return
    }
    onReviewSubmission?.('NEEDS_REVISION', comment)
    setShowSubmissionReviewDialog(false)
    setSubmissionReviewComment('')
  }

  const handleClose = () => {
    void navigate({
      to: backHref as any,
    })
  }

  // Regenerate through the background policy-generation queue. The detail page
  // keeps polling while `getGenerationItems` reports queued/generating.
  const regenerateNowMutation = useMutation(
    trpc.policy.regenerateNow.mutationOptions()
  )
  const { mutateAsync: regenerateNowAsync } = regenerateNowMutation
  const regenerateWith = useCallback(
    async (prose?: 'template' | 'voice') => {
    if (!programId || !item || isPendingApproval) return
    try {
      const result = await (regenerateNowAsync as any)({
        programId,
        policySlug: item.slug,
        ...(prose ? { prose } : {}),
      })
      if (result?.blocked) {
        // RFC-231 §6 Rule 4 — nothing is generated from defaults. Send the
        // user to this policy's questions; the gate lifts once they answer.
        toast.message(
          `${result.unanswered} of ${result.total} questions for this policy are unanswered`,
          { description: 'Answer them, then generate the policy.' }
        )
        void navigate({
          to: '/workspaces/$workspaceId/settings/program',
          params: (prev: Record<string, string>) => prev,
          search: { policy: item.slug },
        } as any)
        return
      }
      if (item.policyDocumentId) {
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
            policyDocumentId: item.policyDocumentId,
          }),
        })
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
            policyDocumentId: item.policyDocumentId,
          }),
        })
      }
      await invalidatePolicyQueries(queryClient)
      // Claim queries — pills won't render without these.
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
          programId,
          policySlug: item.slug,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyClaims.queryKey({
          programId,
          policySlug: item.slug,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.list.queryKey({
          programId,
          policyName: item.slug,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDraftAssumptions.queryKey({
          programId,
          policySlug: item.slug,
        }),
      })
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to regenerate policy'
      )
    }
    },
    [programId, item, isPendingApproval, regenerateNowAsync, queryClient]
  )
  const handleRegenerateImmediate = useCallback(
    () => regenerateWith(),
    [regenerateWith]
  )
  // RFC-231 §6: LLM prose around pinned claims; the claim set is unchanged.
  const handleRewriteInOurVoice = useCallback(
    () => regenerateWith('voice'),
    [regenerateWith]
  )

  // Auto-regenerate when arriving with ?regenerate=true.
  // Enqueues the deterministic v2 regenerate path, then the `?regenerate`
  // param is cleared by the parent route.
  const autoRegenerateTriggered = useRef(false)
  useEffect(() => {
    if (autoRegenerateTriggered.current) return
    if (!autoRegenerate || !programId || !item) return
    if (isPendingApproval) {
      autoRegenerateTriggered.current = true
      onAutoRegenerateTriggered?.()
      return
    }
    autoRegenerateTriggered.current = true
    void handleRegenerateImmediate()
    onAutoRegenerateTriggered?.()
  }, [
    autoRegenerate,
    programId,
    item,
    isPendingApproval,
    handleRegenerateImmediate,
    onAutoRegenerateTriggered,
  ])

  // Issue #1349 — /policy new-version coach tour. DOM-presence predicates encode
  // the state gate: the submit-for-review step only exists for an editable draft
  // (the Request Approval button is absent once approved / while pending).
  // Declared before the loading/not-found early returns below so hook order is
  // stable across renders (moving it after those returns changed the hook count).
  const policyTourSteps = useMemo<CoachStep[]>(
    () => [
      {
        element: '[data-tour="policy-edit-button"]',
        title: 'Revise this policy',
        description:
          'Edit this policy directly. Editing an approved policy starts a new version.',
        side: 'bottom',
        align: 'end',
        when: domAnchorPresent('policy-edit-button'),
      },
      {
        element: '[data-tour="policy-request-approval"]',
        title: 'Submit for review',
        description:
          'When the draft looks right, request approval. The assigned approver signs off before it becomes the live version.',
        side: 'bottom',
        align: 'end',
        when: domAnchorPresent('policy-request-approval'),
      },
    ],
    []
  )
  const policyTour = useCoachTour({
    tourId: 'policy-new-version',
    scopeKey: workspaceId,
    steps: policyTourSteps,
    enabled:
      !panelMode &&
      !isReadonlyMode &&
      !isAuditorCenterPolicy &&
      Boolean(workspaceId),
  })

  const isAuditorPolicyDetailLoading =
    isAuditorCenterPolicy &&
    auditCycleId != null &&
    (isLoading || submissionMetaLoading)

  if (isAuditorPolicyDetailLoading) {
    return (
      <PolicyDetailLoadingShell
        variant='auditor'
        workspaceId={workspaceId}
        auditCycleId={auditCycleId}
        auditsHomeRoute={auditsHomeRoute}
        adminAuditorPreview={adminAuditorPreview}
      />
    )
  }

  if (isLoading) {
    return (
      <PolicyDetailLoadingShell variant='manage' workspaceId={workspaceId} />
    )
  }

  if (!item || !programId) {
    return (
      <div className='text-muted-foreground flex h-full items-center justify-center'>
        Policy not found.
      </div>
    )
  }

  // While the enqueue mutation is in flight, show `generating` locally until
  // the server-driven generationState catches up.
  const displayItem =
    regenerateNowMutation.isPending && item.generationState !== 'generating'
      ? { ...item, generationState: 'generating' as const }
      : item

  // A queued/generating policy is blank because the worker hasn't written yet.
  // Showing the "This policy is blank → Generate with Vera" card through that
  // window contradicts the header's own "Generating" pill and invites a second
  // enqueue on top of the first.
  const isDraftingInBackground =
    displayItem.generationState === 'queued' ||
    displayItem.generationState === 'generating'

  // RFC-191: this slot's content lives in a consolidated master doc (its own
  // content was cleared). Resolve the master item from the same items list so
  // the covered panel can show the master's accountability + link to it.
  const coveredMasterItem = displayItem.coveredByDocumentId
    ? (items.find(
        (i) => i.policyDocumentId === displayItem.coveredByDocumentId
      ) ?? null)
    : null
  const coveredMasterSlug = coveredMasterItem?.slug ?? null

  const policyDetailHeaderProps = {
    item: displayItem,
    policyData,
    programId,
    editorRef,
    isEditing,
    editLocked: isLockedByAnotherUser,
    editLockedByName: policyEditLockStatusQuery.data?.lockedByName ?? null,
    onEditingChange: async (editing: boolean) => {
      if (editing) {
        const ok = await beginPolicyEdit()
        if (!ok) return false
        setViewingVersion(null)
        return true
      }
      return endPolicyEdit()
    },
    onClose: handleClose,
    viewingVersion,
    onViewingVersionChange: (v: number | null) => {
      setViewingVersion(v)
    },
    onOpenVersionDiff: () => {
      setCompareDialogOpenRequest((value) => value + 1)
    },
    claims: headerClaims as any,
    showClaims,
    onShowClaimsChange: setShowClaims,
    onApprove: async () => {
      // Guard the flush window: `approveMutation.isPending` only flips true
      // after the flush, so without this a second click could double-approve.
      if (isFinalizingApproval || approveMutation.isPending) return
      setIsFinalizingApproval(true)
      try {
        // Flush unsaved edits before approving so the snapshot includes them.
        await flushPendingPolicySave()
        if (approvalGateEnabled) {
          setApproveDialogOpen(true)
        } else {
          // Flag off → pre-existing one-click approve (no readiness dialog).
          await handleApprove()
        }
      } catch (err: any) {
        toast.error('Failed to approve: ' + (err?.message ?? 'Unknown error'))
      } finally {
        setIsFinalizingApproval(false)
      }
    },
    isApproving: approveMutation.isPending || isFinalizingApproval,
    onBeforeRequestApproval: flushPendingPolicySave,
    onApprovalRequested: () => {
      clearActivePolicyEditLock()
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyEditLockStatus.queryKey({
          policyDocumentId: item.policyDocumentId!,
        }),
      })
    },
    onRegenerateImmediate: handleRegenerateImmediate,
    onRewriteInOurVoice: handleRewriteInOurVoice,
    isRegeneratingImmediate: regenerateNowMutation.isPending,
    onUploadNewVersion: () => versionUploadInputRef.current?.click(),
    isUploadingVersion: uploadingSlug === item.slug,
    onOpenPolicyLineage: () => setShowPolicyLineage(true),
    isPolicyLineageLoading: policyLineageQuery.isLoading,
    readOnly: isReadonlyMode,
    submittedVersion: submissionMeta?.submittedVersion,
    saveStatus,
  }

  // AI-drafted policies need a human pass before approval. Show while the
  // current content is still AI-authored (GENERATED, or IMPORTED_THEN_AI where
  // AI rewrote an upload) — NOT once a human has edited it (AI_THEN_EDITED) —
  // unapproved, and we're viewing the live current version (not history / a
  // submission review). Rendered inside PolicyDetailPanel's main column (via
  // `mainBanner`) so the strip spans only the document area, not the right panel.
  const draftBanner =
    isDraftingInBackground && mode !== 'readonly' ? (
      <div
        role='status'
        className='flex items-start gap-2 border-b border-[#F0E4C8] bg-[#FBF6E9] px-6 py-2.5'
      >
        <Loader2 className='text-status-orange mt-0.5 h-4 w-4 shrink-0 animate-spin' />
        <span className='font-body text-[12px] leading-snug text-[#8A6D1F]'>
          {(regenerateNowMutation.variables as { prose?: string } | undefined)
            ?.prose === 'voice'
            ? `Rewriting ${displayItem.title} in your voice (claims stay pinned)…`
            : `${displayItem.isGenerated ? 'Regenerating' : 'Generating'} ${displayItem.title}…`}
        </span>
      </div>
    ) : (displayItem.source === 'GENERATED' ||
      displayItem.source === 'IMPORTED_THEN_AI') &&
    displayItem.isGenerated &&
    displayItem.generationState !== 'approved' &&
    viewingVersion === null &&
    mode !== 'readonly' ? (
      <div className='flex items-start gap-2 border-b border-[#F0E4C8] bg-[#FBF6E9] px-6 py-2.5'>
        <Sparkles className='text-status-orange mt-0.5 h-4 w-4 shrink-0' />
        <span className='font-body text-[12px] leading-snug text-[#8A6D1F]'>
          Vera drafted this policy from your setup answers.{' '}
          <span className='font-medium'>Review it carefully</span> and edit
          anything that doesn’t match how you actually operate before approving.
          You’re accountable for its accuracy.
        </span>
      </div>
    ) : null
  const editLockBanner = policyEditLockBannerText ? (
    <div className='border-status-red/30 bg-status-red-bg flex items-start gap-2 border-b px-6 py-2.5'>
      <AlertTriangle className='text-status-red mt-0.5 h-4 w-4 shrink-0' />
      <span className='font-body text-[12px] leading-snug text-[#8A1F1F]'>
        {policyEditLockBannerText}
      </span>
    </div>
  ) : null
  const policyCommentsEnabled =
    enableComments && !isAuditorCenterPolicy && (isAdmin || isOwner)
  const currentEditorBanner = isEditing ? (
    <div className='border-border bg-card flex h-9 items-center gap-4 border-b px-6'>
      {policyEditorToolbar ? (
        <EditorContext.Provider
          value={{ editor: policyEditorToolbar.editor } as EditorContextValue}
        >
          <div className='min-w-0 flex-1 [&_.tiptap-button]:!h-7 [&_.tiptap-toolbar]:static [&_.tiptap-toolbar]:!min-h-9 [&_.tiptap-toolbar]:!border-0 [&_.tiptap-toolbar]:!bg-transparent [&_.tiptap-toolbar]:!px-0'>
            <MainToolbar
              editor={policyEditorToolbar.editor}
              enableComments={policyCommentsEnabled}
              onOpenComments={policyEditorToolbar.onOpenComments}
              enableColorAndAlignment
                onAskVera={policyEditorToolbar.onAskVera}
              onAddClaimFromSelection={
                policyEditorToolbar.onAddClaimFromSelection
              }
            />
          </div>
        </EditorContext.Provider>
      ) : (
        <div className='flex-1' />
      )}
      <button
        type='button'
        onClick={() => {
          void endPolicyEdit()
        }}
        disabled={isEndingPolicyEdit}
        className='border-status-blue/30 text-status-blue hover:bg-status-blue-bg inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm border bg-card px-2.5 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'
      >
        {isEndingPolicyEdit ? (
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
        ) : (
          <Save className='h-3.5 w-3.5' />
        )}
        End editing
      </button>
    </div>
  ) : null
  const editorMainBanner =
    editLockBanner || currentEditorBanner || draftBanner ? (
      <>
        {editLockBanner}
        {currentEditorBanner}
        {draftBanner}
      </>
    ) : null

  const policyDetailBody = (
    <>
      <div className={POLICY_DETAIL_PAGE_BODY_CLASS}>
        {/* The owner/approver/review metadata strip moved into the right panel's
          "Properties" tab (PolicyDetailPanel). For a covered slot, the covered
          panel below shows the master's accountability instead. */}
        {/* Hidden input backing the header's "Upload your own version" action. */}
        <input
          ref={versionUploadInputRef}
          type='file'
          accept={POLICY_UPLOAD_ACCEPT}
          className='hidden'
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f && item.slug) void uploadPolicyVersion(item.slug, f)
            e.target.value = ''
          }}
        />
        {policySlug === 'system-description' && programId && (
          <SystemDescriptionTemplateBanner programId={programId} />
        )}
        {displayItem.coveredByDocumentId ? (
          <div className='flex flex-1 items-start justify-center overflow-auto px-6 py-10'>
            <div className='w-full max-w-[805px] rounded-md border border-status-green-border-soft bg-[#F6FBF7] px-6 py-6'>
              <div className='flex items-center gap-2'>
                <Check className='h-4 w-4 shrink-0 text-status-green-emphasis' />
                <span className='font-heading text-text-secondary text-[14px] font-semibold'>
                  Covered by {displayItem.coveredByTitle ?? 'another policy'}
                </span>
              </div>
              <p className='font-body text-text-body mt-2 text-[12px] leading-relaxed'>
                You consolidated this policy into a single document, so its
                content and approval live there. The requirements below are
                satisfied by that document, so there’s nothing to write here.
              </p>
              {displayItem.frameworkRefs.length > 0 && (
                <div className='mt-3'>
                  <div className='font-body text-text-secondary text-[10px] font-semibold tracking-[0.6px] uppercase'>
                    Requirements this policy satisfies
                  </div>
                  <div className='mt-1.5 flex flex-wrap items-center gap-1'>
                    {displayItem.frameworkRefs.flatMap((fr) =>
                      fr.refs.map((ref) => (
                        <span
                          key={`${fr.framework}:${ref}`}
                          className='font-heading inline-flex items-center rounded-sm border border-cobalt-border-soft bg-primary-subtle px-1.5 py-0.5 text-[9px] font-medium text-cobalt-strong'
                        >
                          {fr.framework}:{ref}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
              {/* RFC-191 (Q2): accountability follows the master — show its owner /
                approver / review here instead of letting them be set on the
                covered slot. */}
              {coveredMasterItem &&
                (coveredMasterItem.ownerRoleName ||
                  coveredMasterItem.approverRoleName ||
                  coveredMasterItem.reviewFrequency) && (
                  <div className='mt-3'>
                    <div className='font-body text-text-secondary text-[10px] font-semibold tracking-[0.6px] uppercase'>
                      Managed on {displayItem.coveredByTitle ?? 'the master'}
                    </div>
                    <div className='font-body text-text-secondary mt-1 flex flex-col gap-0.5 text-[12px]'>
                      <div>
                        Owner:{' '}
                        {coveredMasterItem.ownerRoleName ?? 'Not assigned'}
                      </div>
                      <div>
                        Approver:{' '}
                        {coveredMasterItem.approverRoleName ?? 'Not assigned'}
                      </div>
                      {/* @deprecated RFC-195 §3.1 — `reviewFrequency` is the legacy
                        per-policy field, no longer the review-cadence source of
                        truth (the consolidated policy_review CES is). Read-only
                        label only; safe to drop in the same cleanup that removes
                        PolicyMetadataBar. */}
                      {coveredMasterItem.reviewFrequency && (
                        <div>Review: {coveredMasterItem.reviewFrequency}</div>
                      )}
                    </div>
                  </div>
                )}
              {coveredMasterSlug && (
                <button
                  type='button'
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/policies/$policySlug',
                      params: { workspaceId, policySlug: coveredMasterSlug },
                      search: testId ? { testId } : undefined,
                    })
                  }
                  className='font-body bg-primary text-primary-foreground hover:bg-primary/90 mt-4 inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] font-medium'
                >
                  View {displayItem.coveredByTitle ?? 'the policy'}
                </button>
              )}
            </div>
          </div>
        ) : !versionsReady ? (
          <PolicyDetailDocumentSkeleton embedded embedInOverlay={panelMode} />
        ) : (
          <PolicyDetailPanel
            item={displayItem}
            policyData={policyData}
            workspaceId={workspaceId}
            auditCycleId={auditCycleId}
            programId={programId}
            mode={mode}
            isEditing={isEditing}
            editorRef={editorRef}
            onEditorCreate={setPolicyEditorToolbar}
            embedInOverlay={panelMode}
            mainBanner={editorMainBanner}
            submissionMetaBar={
              visibleSubmissionMeta ? (
                <PolicySubmissionMetaBar
                  submission={visibleSubmissionMeta}
                  showInlineCommentHint={Boolean(auditCycleId)}
                  draftVersion={
                    !auditCycleId &&
                    viewingVersion === null &&
                    isCurrentVersionDraft
                      ? (currentVersionFromQuery ?? null)
                      : null
                  }
                  onViewApprovedVersion={
                    !auditCycleId &&
                    visibleVersionNumber !== null &&
                    visibleVersionNumber !==
                      visibleSubmissionMeta.submittedVersion
                      ? () => {
                          void (async () => {
                            const canLeaveEditor = await endPolicyEdit()
                            if (!canLeaveEditor) return
                            setViewingVersion(
                              visibleSubmissionMeta.submittedVersion
                            )
                          })()
                        }
                      : undefined
                  }
                  onReview={
                    onReviewSubmission
                      ? () => setShowSubmissionReviewDialog(true)
                      : undefined
                  }
                  inline
                />
              ) : null
            }
            disableApplyBlockListener
            enableComments={policyCommentsEnabled}
            hideCommentComposer={hideCommentComposer}
            claims={
              isReadonlyMode ? (submittedSnapshotClaims as any) : undefined
            }
            viewingVersion={viewingVersion}
            focusedClaimId={focusedClaimId}
            focusedClaimBlockId={focusedClaimBlockId}
            showClaims={showClaims}
            onShowClaimsChange={setShowClaims}
            showHighlights={showHighlights}
            onShowHighlightsChange={setShowHighlights}
            versionContent={
              viewingVersion !== null
                ? ((versionContentQuery.data as any) ?? null)
                : null
            }
            versionLoading={
              versionContentQuery.isLoading && viewingVersion !== null
            }
            versionError={
              viewingVersion !== null
                ? (versionContentQuery.error?.message ?? null)
                : null
            }
            versions={displayVersionsData}
            compareVersion={compareVersion}
            onCompareVersionChange={setCompareVersion}
            compareDialogOpenRequest={compareDialogOpenRequest}
            compareVersionContent={
              isComparingVersions
                ? ((compareVersionContentQuery.data as any) ?? null)
                : null
            }
            compareVersionLoading={
              isComparingVersions && compareVersionContentQuery.isLoading
            }
            compareVersionError={
              isComparingVersions
                ? (compareVersionContentQuery.error?.message ?? null)
                : null
            }
            submittedVersion={submissionMeta?.submittedVersion ?? null}
            submittedVersionContent={
              isReadonlyMode && viewingVersion === null
                ? (isAuditorCenterPolicy ? auditorPolicy : (submittedVersionContentQuery.data as any))
                : null
            }
            submittedVersionLoading={
              isReadonlyMode &&
              viewingVersion === null &&
              submittedVersionContentQuery.isLoading
            }
            submittedVersionError={
              isReadonlyMode && viewingVersion === null
                ? (submittedVersionContentQuery.error?.message ?? null)
                : null
            }
            submissionMeta={submissionMeta}
            preferredSidebarTab={preferredSidebarTab}
            onSidebarTabChange={setPreferredSidebarTab}
            onOpenCommentThreadCountChange={setOpenCommentThreadCount}
            onRevert={handleRevert}
            revertPending={revertMutation.isPending}
            onEditorUpdate={handleEditorUpdate}
            onLiveContentLoaded={(policyDocumentId) => {
              loadedPolicyDocumentIdRef.current = policyDocumentId
            }}
          />
        )}
      </div>
      <PolicyGalaxyLineageDialog
        open={showPolicyLineage}
        onOpenChange={setShowPolicyLineage}
        workspaceId={workspaceId}
        data={policyLineageQuery.data}
        isLoading={policyLineageQuery.isLoading}
        error={policyLineageQuery.error?.message}
        readOnly={isReadonlyMode}
      />
      <Dialog
        open={showSubmissionReviewDialog}
        onOpenChange={(open) => {
          setShowSubmissionReviewDialog(open)
          if (!open) {
            setSubmissionReviewComment('')
          }
        }}
      >
        <DialogContent className='!max-w-[600px]'>
          <DialogHeader>
            <DialogTitle>Review policy</DialogTitle>
            <DialogDescription>
              Approve this policy version for the audit or request changes from
              the workspace.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 px-6 py-2'>
            <div className='space-y-1 text-sm'>
              <div className='flex justify-between gap-4'>
                <span className='text-text-subtle'>Approved version</span>
                <span className='font-medium'>
                  v{submissionMeta?.submittedVersion ?? '—'}
                </span>
              </div>
              <div className='flex justify-between gap-4'>
                <span className='text-text-subtle'>Current status</span>
                <span className='font-medium'>
                  {submissionMeta
                    ? submissionMeta.submissionStatus
                      ? getAuditorSubmissionStatusPresentation(
                          submissionMeta.submissionStatus
                        ).metaLabel
                      : 'Not reviewed'
                    : '—'}
                </span>
              </div>
              {submissionMeta?.submittedBy && (
                <div className='flex justify-between gap-4'>
                  <span className='text-text-subtle'>Submitted by</span>
                  <span className='font-medium'>
                    {submissionMeta.submittedBy}
                  </span>
                </div>
              )}
            </div>

            <Textarea
              placeholder='Add review comments...'
              value={submissionReviewComment}
              onChange={(e) => setSubmissionReviewComment(e.target.value)}
              className='min-h-[100px] text-sm'
            />
          </div>
          <DialogFooter className='gap-2 sm:gap-2'>
            <button
              type='button'
              onClick={() => setShowSubmissionReviewDialog(false)}
              className='border-border text-text-body inline-flex items-center justify-center rounded-md border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-surface-muted'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={handleRequestSubmissionChanges}
              disabled={reviewSubmissionPending}
              className='border-border text-text-body inline-flex items-center justify-center rounded-md border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-surface-muted disabled:opacity-60'
            >
              {reviewSubmissionPending ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : null}
              Request Changes
            </button>
            <button
              type='button'
              onClick={handleApproveSubmission}
              disabled={reviewSubmissionPending}
              className='inline-flex items-center justify-center rounded-md bg-status-green px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60'
            >
              {reviewSubmissionPending ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : null}
              Approve
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  const policyPageLayout = (
    <ScreenataPageLayout
      title={item.title}
      // In panel/overlay preview, hide the page-level "Ask Vera" button.
      hideAskAi={panelMode}
      mainClassName={
        panelMode
          ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden'
          : 'flex h-[calc(var(--app-vh)_-_50px)] min-h-0 flex-col overflow-hidden'
      }
      breadcrumb={
        isAuditorCenterPolicy ? (
          <AuditorCycleBreadcrumb
            workspaceId={workspaceId}
            auditsHomeRoute={auditsHomeRoute}
            items={[
              {
                label: 'Policies',
                to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/policies',
                params: { workspaceId, auditCycleId },
              },
              { label: item.title, current: true },
            ]}
          />
        ) : testId ? (
          <Link
            to='/workspaces/$workspaceId/controls'
            params={{ workspaceId }}
            search={{ test: testId }}
            className='font-heading text-text-subtle hover:text-foreground text-[15px] font-medium'
          >
            Back to Test
          </Link>
        ) : (
          <Link
            to='/workspaces/$workspaceId/policies'
            params={{ workspaceId }}
            className='font-heading text-text-subtle text-[15px] font-medium hover:text-cobalt-strong'
          >
            Policies
          </Link>
        )
      }
      breadcrumbIsTerminal={isAuditorCenterPolicy}
      headerExtra={
        <PolicyDetailHeader
          {...policyDetailHeaderProps}
          renderMode='overflow'
          transparentOverflowMenu={!!adminAuditorPreview}
        />
      }
      headerActions={
        <div className='flex items-center gap-2'>
          {!isAuditorCenterPolicy && item.policyDocumentId && (
            <PolicyReviewAction policyDocumentId={item.policyDocumentId} policyTitle={item.title} reviewTarget={workingPolicy?.reviewTarget ?? null} onViewVersion={setViewingVersion} />
          )}
          {auditCycleId && auditorPolicy && <AuditorPolicyReviewHistory auditCycleId={auditCycleId} source='policy' sourceId={auditorPolicy.slug} title={auditorPolicy.title} records={auditorPolicy.reviewRecords} />}
          {/* {policyTour.hasApplicableSteps && (
            <button
              type='button'
              onClick={policyTour.replay}
              className='font-body inline-flex items-center gap-1 text-[11px] text-text-placeholder transition-colors hover:text-cobalt-strong'
            >
              <Compass className='h-3 w-3' /> Show me how
            </button>
          )} */}
          <PolicyDetailHeader
            {...policyDetailHeaderProps}
            renderMode='actions'
            afterSaveStatus={
              viewingVersion === null && openCommentThreadCount > 0 ? (
                <button
                  type='button'
                  onClick={() => {
                    setPreferredSidebarTab('comments')
                    setShowClaims(false)
                  }}
                  className='border-status-orange/30 bg-status-orange-bg text-status-orange-strong hover:bg-status-orange-bg/80 inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm border px-2.5 text-[12px] font-medium transition-colors'
                >
                  <MessageSquare className='h-3.5 w-3.5' />
                  {openCommentThreadCount} review notes need attention
                </button>
              ) : null
            }
          />
        </div>
      }
      raw
    >
      {policyDetailBody}
      <CoachTour controller={policyTour} />
      {programId && approvalGateEnabled ? (
        <ApprovePolicyDialog
          open={approveDialogOpen}
          onOpenChange={setApproveDialogOpen}
          programId={programId}
          policySlug={policySlug}
          policyTitle={item.title}
          isApproving={approveMutation.isPending}
          onApprove={handleApprove}
        />
      ) : null}
    </ScreenataPageLayout>
  )

  if (panelMode) {
    return (
      <div className='flex h-full min-h-0 flex-col overflow-hidden'>
        {policyPageLayout}
      </div>
    )
  }

  return policyPageLayout
}
